import config from "../../config";
import { useAppDispatch, useAppSelector } from "../../Hooks/useStore";
import { DefaultTheme } from "styled-components";
import { Theme, themes } from "../../StyleSheet";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { useCallback } from "react";
import { getThemeFromCookie, setThemeCookie } from "../../Utils/cookieUtils";

export type ThemeState = {
  name: Theme;
};

// Initialize theme from cookie or default
export const initializeTheme = (): ThemeState => {
  const cookieTheme = getThemeFromCookie() as Theme | undefined;
  const initialTheme = cookieTheme && themes[cookieTheme] ? cookieTheme : config.defaultTheme;

  if (!cookieTheme) {
    setThemeCookie(initialTheme);
  }

  return {
    name: initialTheme,
  };
};

export const themeInitialState: ThemeState = initializeTheme();

export const themeSlice = createSlice({
  name: "themeSlice",
  reducers: {
    setTheme: (state, action: PayloadAction<Theme>) => {
      state.name = action.payload;
      // Set cookie immediately for subdomain compatibility
      setThemeCookie(action.payload);
    },
  },
  initialState: themeInitialState,
});

// ✅ Hooks
export function useTheme(): ThemeState {
  return useAppSelector((state) => state.theme);
}

export function useThemeColors(): [any, Theme] {
  const { name } = useTheme();
  const cookieTheme = getThemeFromCookie() as Theme | undefined;

  if (cookieTheme) {
    const colors = themes[cookieTheme];
    return [colors, name];
  } else {
    const colors = themes[name];
    return [colors, name];
  }
}

export const useChangeTheme = () => {
  const dispatch = useAppDispatch();

  const dispatchChangeTheme = useCallback(
    (theme: Theme) => {
      dispatch(setTheme(theme));
    },
    [dispatch],
  );

  return { dispatchChangeTheme };
};

export const { setTheme } = themeSlice.actions;
export default themeSlice.reducer;
